// @givmo/app-admin — Steps Competitions decode/display helpers (EP-WEB10).
//
// Pure, side-effect-free helpers for the company-admin steps-competition surface (backend
// `/admin/company/steps-competitions*`, SC-BE3). Kept as their own no-React module so the list/entry
// decode, the status mapping, the prize-hold display law, and the prize validation are unit-testable in
// isolation (matching the `company-fundraisers-decode.ts` / `give-decode.ts` split).
//
// Tolerant reader: both list decodes go through the shared `extractList`, reading BOTH a bare row array
// AND a `{ data: [...] }` envelope and degrading to `[]` rather than throwing. Both admin list routes DO
// return the paginated envelope, but the api-client response middleware unwraps it to the bare array
// before the query sees it — so the generated `…PaginatedResponse` type is a compile-time fiction at
// runtime and only `extractList` reads correctly in both worlds. Unknown enum values are rendered
// neutrally rather than dropped (a backend that adds a status must never blank a row).
//
// MONEY: `prize_amount` / `company_wallet_*` cross the wire as decimal STRINGS. Every figure here is
// parsed to EXACT integer cents (`parseCentsAmount`) and formatted from cents (`formatCentsUsd`) — never
// `formatUsd`, never float arithmetic on the wire string. Vocabulary is "held" / "available", matching
// the wire model and the shipped iOS admin surface.

import { extractList } from '@givmo/api-client/list';
import {
  firstErrorCode,
  firstErrorContext,
  isGivmoError,
  type GivmoError,
} from '@givmo/api-client';
import type {
  AdminEntryOut,
  AdminStepsCompetitionOut,
  CreateBody,
  StepsCompetitionEntryStatus,
  StepsCompetitionStatus,
  StepsCompetitionVisibility,
} from '@givmo/api-client';
import { formatCentsUsd, parseCentsAmount } from '../dashboard/dashboard-decode';

// ── List decode ────────────────────────────────────────────────────────────────────────────────

/** Tolerant decode of `GET /admin/company/steps-competitions` (DRAFTs included). Never throws. */
export function extractCompetitions(body: unknown): AdminStepsCompetitionOut[] {
  return extractList<AdminStepsCompetitionOut>(body);
}

/** Tolerant decode of `GET /admin/company/steps-competitions/{id}/entries` (TRUE identities). */
export function extractEntries(body: unknown): AdminEntryOut[] {
  return extractList<AdminEntryOut>(body);
}

// ── Status ─────────────────────────────────────────────────────────────────────────────────────

export type StatusTone = 'success' | 'neutral' | 'warning' | 'info' | 'danger';

export interface StatusView {
  label: string;
  tone: StatusTone;
}

/**
 * The competition status badge view. An unknown value degrades to a NEUTRAL chip carrying the raw
 * string (tolerant reader — a backend that adds a lifecycle state must never crash or blank a row).
 */
export function competitionStatusView(
  status: StepsCompetitionStatus | string | null | undefined,
): StatusView {
  switch (status) {
    case 'DRAFT':
      return { label: 'Draft', tone: 'neutral' };
    case 'PUBLISHED':
      return { label: 'Published', tone: 'info' };
    case 'ACTIVE':
      return { label: 'Active', tone: 'success' };
    case 'GRACE':
      return { label: 'Grace period', tone: 'warning' };
    case 'PAYOUT_PENDING':
      return { label: 'Payout pending', tone: 'warning' };
    case 'FINALIZED':
      return { label: 'Finalized', tone: 'success' };
    case 'CANCELED':
      return { label: 'Canceled', tone: 'neutral' };
    default:
      return { label: unknownLabel(status), tone: 'neutral' };
  }
}

/** The entry status badge view. Same tolerant-unknown rule as {@link competitionStatusView}. */
export function entryStatusView(
  status: StepsCompetitionEntryStatus | string | null | undefined,
): StatusView {
  switch (status) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'success' };
    case 'WITHDRAWN':
      return { label: 'Withdrawn', tone: 'neutral' };
    case 'DISQUALIFIED':
      return { label: 'Disqualified', tone: 'danger' };
    default:
      return { label: unknownLabel(status), tone: 'neutral' };
  }
}

/** Render an unrecognized enum value as itself (trimmed), or "Unknown" when it carries no text. */
function unknownLabel(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : 'Unknown';
}

export function isDraft(competition: AdminStepsCompetitionOut): boolean {
  return competition.status === 'DRAFT';
}

/** Publish is offered only on a DRAFT (the backend is authoritative; this gates the control). */
export function canPublish(competition: AdminStepsCompetitionOut): boolean {
  return competition.status === 'DRAFT';
}

/**
 * Cancel releases the hold. The backend allows it from ANY pre-FINALIZED state — `_CANCELABLE_STATUSES`
 * = DRAFT · PUBLISHED · ACTIVE · GRACE · **PAYOUT_PENDING** (FINALIZED 409s: its hold was consumed).
 * PAYOUT_PENDING matters: that is the state where a payout is stuck on an unavailable charity with the
 * prize still held, so cancel is the admin's escape hatch there, not a control to hide.
 */
export function canCancel(competition: AdminStepsCompetitionOut): boolean {
  return (
    competition.status === 'DRAFT' ||
    competition.status === 'PUBLISHED' ||
    competition.status === 'ACTIVE' ||
    competition.status === 'GRACE' ||
    competition.status === 'PAYOUT_PENDING'
  );
}

/** Only a PAYOUT_PENDING competition is retryable (the backend 409s otherwise). */
export function canRetryPayout(competition: AdminStepsCompetitionOut): boolean {
  return competition.status === 'PAYOUT_PENDING';
}

/** Entries only exist once people can join — a DRAFT has none, so the panel stays hidden. */
export function hasEntrantSurface(competition: AdminStepsCompetitionOut): boolean {
  return competition.status !== 'DRAFT';
}

export function visibilityLabel(
  visibility: StepsCompetitionVisibility | string | null | undefined,
): string {
  switch (visibility) {
    case 'EMPLOYEES_ONLY':
      return 'Employees only';
    case 'PUBLIC':
      return 'Public';
    default:
      return unknownLabel(visibility);
  }
}

// ── Money (cents-exact; "held" / "available" vocabulary) ────────────────────────────────────────

/** Format a wire money STRING as USD from exact integer cents. Unparseable / absent → "—". */
export function formatMoney(wire: string | null | undefined): string {
  return formatCentsUsd(parseCentsAmount(wire ?? ''));
}

/** This competition's prize, formatted. The prize is immutable once created. */
export function formatPrize(competition: AdminStepsCompetitionOut): string {
  return formatMoney(competition.prize_amount);
}

/** The company wallet's AVAILABLE balance (detail/publish/cancel reads only), or null when absent. */
export function walletAvailable(competition: AdminStepsCompetitionOut): string | null {
  return competition.company_wallet_available_balance == null
    ? null
    : formatMoney(competition.company_wallet_available_balance);
}

/**
 * The company wallet's held aggregate — the Σ of ALL active holds across every competition. ALWAYS
 * labelled as the WALLET figure by its caller; it is NOT this competition's hold (see
 * {@link prizeHoldView}). Null when the wire omits it (list reads carry no money context).
 */
export function walletHeldAggregate(competition: AdminStepsCompetitionOut): string | null {
  return competition.company_wallet_held_amount == null
    ? null
    : formatMoney(competition.company_wallet_held_amount);
}

/** Whether the wallet's available balance covers this competition's prize. Null when unknowable. */
export function walletCoversPrize(competition: AdminStepsCompetitionOut): boolean | null {
  const available = parseCentsAmount(competition.company_wallet_available_balance ?? '');
  const prize = parseCentsAmount(competition.prize_amount ?? '');
  if (available === null || prize === null) return null;
  return available >= prize;
}

// ── The prize-hold display law (mirrors the shipped iOS admin surface, SC-IOS4) ──────────────────

/**
 * The per-competition prize-hold state line, driven by STATUS (never by `prize_hold_id`, which
 * persists as a denorm after the hold is consumed or released).
 *
 * The law, ported verbatim from the iOS admin detail rather than re-derived:
 *   - DRAFT                                   → not yet held; name the publish-time hold moment.
 *   - PUBLISHED / ACTIVE / GRACE / PAYOUT_PENDING
 *                                             → "Held for this prize" = THIS competition's own
 *                                               `prize_amount` (the backend hold cost is fee-free, so
 *                                               it equals the prize exactly) — NEVER the wallet
 *                                               aggregate, which is a different number and is always
 *                                               labelled as the wallet's.
 *   - FINALIZED                               → paid to the winner's charity.
 *   - CANCELED                                → released back to available.
 *   - unknown                                 → no line at all (tolerant reader: say nothing rather
 *                                               than assert a money state we cannot know).
 */
export type PrizeHoldView =
  | { kind: 'will-hold'; note: string }
  | { kind: 'held'; label: string; amount: string; note: string }
  | { kind: 'paid'; note: string }
  | { kind: 'released'; note: string }
  | { kind: 'none' };

export function prizeHoldView(competition: AdminStepsCompetitionOut): PrizeHoldView {
  const prize = formatPrize(competition);
  switch (competition.status) {
    case 'DRAFT':
      return {
        kind: 'will-hold',
        note:
          `Publishing holds ${prize} from your company balance so the prize is guaranteed. ` +
          'The prize is locked once published.',
      };
    case 'PUBLISHED':
    case 'ACTIVE':
    case 'GRACE':
    case 'PAYOUT_PENDING':
      return {
        kind: 'held',
        label: 'Held for this prize',
        amount: prize,
        note: 'This amount is held from your company balance and is released if you cancel.',
      };
    case 'FINALIZED':
      return { kind: 'paid', note: 'The prize was paid to the winner’s charity.' };
    case 'CANCELED':
      return {
        kind: 'released',
        note: 'The prize hold was released back to your available balance.',
      };
    default:
      return { kind: 'none' };
  }
}

/**
 * The degenerate-competition rule (SC-FU3), surfaced wherever we would otherwise imply a guaranteed
 * winner: fewer than 2 ACTIVE entries with steps > 0 at finalize → NO payout and the hold is released.
 * Never promise a winner.
 */
export const DEGENERATE_RULE_COPY =
  'If fewer than two entrants finish with steps recorded, no prize is awarded and the held amount is ' +
  'released back to your available balance.';

// ── Prize floor (SC-FU3 client mirror; the backend 422 is the truth) ─────────────────────────────

/** The product floor, in exact integer cents. The backend rejects below this with 422. */
export const MIN_PRIZE_CENTS = 1000;
/** The floor as display copy — derived from {@link MIN_PRIZE_CENTS} so the two can never drift. */
export const MIN_PRIZE_TEXT = formatCentsUsd(MIN_PRIZE_CENTS);
export const MIN_PRIZE_HINT = `${MIN_PRIZE_TEXT} minimum`;

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 2000;

export type PrizeParse = { value: string } | { error: string };

/**
 * Parse + validate the prize input. Returns the NORMALIZED DECIMAL STRING sent to the wire (never a
 * float) or a message. Mirrors the backend's $10 floor for fast feedback — the backend still enforces
 * it (422 `PRIZE_BELOW_MINIMUM`), and that error is surfaced verbatim if it ever disagrees.
 */
export function parsePrizeDollars(raw: string): PrizeParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { error: 'Enter a prize amount.' };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return { error: 'Enter a dollar amount like 250 or 250.00.' };
  }
  const cents = parseCentsAmount(trimmed);
  if (cents === null) return { error: 'Enter a dollar amount like 250 or 250.00.' };
  if (cents < MIN_PRIZE_CENTS) return { error: `The prize must be at least ${MIN_PRIZE_TEXT}.` };
  return { value: trimmed };
}

/** Whether the current prize input is below the floor — drives the hint's error styling as you type. */
export function prizeIsBelowMinimum(raw: string): boolean {
  const cents = parseCentsAmount(raw.trim());
  return cents !== null && cents < MIN_PRIZE_CENTS;
}

// ── Dates ────────────────────────────────────────────────────────────────────────────────────────

/** The wire date format for `start_date` / `end_date` — a plain calendar day, matching iOS. */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDay(value: string): boolean {
  return ISO_DAY.test(value.trim());
}

/** The numeric parts of a wire calendar day, or null when it is not one. */
function isoDayParts(value: string | null | undefined): [number, number, number] | null {
  const match = ISO_DAY.exec((value ?? '').trim());
  return match === null ? null : [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** Validate the competition window. Returns a message, or null when the pair is acceptable. */
export function validateDateRange(startDate: string, endDate: string): string | null {
  const start = startDate.trim();
  const end = endDate.trim();
  if (!isIsoDay(start)) return 'Choose a start date.';
  if (!isIsoDay(end)) return 'Choose an end date.';
  // Both are zero-padded ISO days, so a lexicographic compare IS a chronological compare.
  if (end < start) return 'The end date must be on or after the start date.';
  return null;
}

/**
 * Serialize a picked calendar day (the design-system `DatePicker` emits a local-midnight `Date`) to the
 * bare `YYYY-MM-DD` the wire wants — LOCAL components, never `toISOString()`, so a picked day never
 * shifts across the UTC boundary (the same rule the super-admin date pickers use).
 */
export function pickedDayToIsoDay(day: Date): string {
  const y = day.getFullYear().toString().padStart(4, '0');
  const m = (day.getMonth() + 1).toString().padStart(2, '0');
  const d = day.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The inverse: a wire `YYYY-MM-DD` back to a LOCAL-midnight `Date` for the picker. Junk → null. */
export function isoDayToPickedDay(value: string | null | undefined): Date | null {
  const parts = isoDayParts(value);
  if (parts === null) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format a wire calendar day (`2026-08-01`) as `Aug 1, 2026`. Junk / absent → "—". */
export function formatDay(value: string | null | undefined): string {
  const parts = isoDayParts(value);
  if (parts === null) return '—';
  const [y, m, d] = parts;
  // Construct in UTC and format in UTC: a calendar day has no timezone, and a local-time construction
  // would shift it a day west of Greenwich.
  const date = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(date.getTime())) return '—';
  return DAY_FORMAT.format(date);
}

const DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** Format an ISO timestamp as its calendar day (`Aug 1, 2026`). Junk / absent → "—". */
export function formatTimestampDay(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (raw === '') return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return DAY_FORMAT.format(date);
}

// ── Entries ──────────────────────────────────────────────────────────────────────────────────────

const STEPS_FORMAT = new Intl.NumberFormat('en-US');

export function formatSteps(steps: number | null | undefined): string {
  return typeof steps === 'number' && Number.isFinite(steps) ? STEPS_FORMAT.format(steps) : '0';
}

export function flaggedDayCount(entry: AdminEntryOut): number {
  const count = entry.flagged_day_count;
  return typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0;
}

/** The entrant's display name, or a neutral placeholder — the admin view carries TRUE identities. */
export function entrantName(entry: AdminEntryOut): string {
  const name = entry.name?.trim();
  return name !== undefined && name.length > 0 ? name : 'Entrant';
}

export function entryCharityName(entry: AdminEntryOut): string {
  const name = entry.charity_name?.trim();
  return name !== undefined && name.length > 0 ? name : '—';
}

export function isDisqualified(entry: AdminEntryOut): boolean {
  return entry.status === 'DISQUALIFIED';
}

/**
 * The two integrity actions are gated on the EXACT status they can act on, not on each other's
 * negation. Only an ACTIVE entry can be disqualified: a WITHDRAWN entrant already left the
 * competition, so offering "Disqualify" there is a control whose only outcome is a 409. Anything
 * unrecognized gets NO action either — the same neutral posture the status badge takes, since we
 * cannot know what a future status permits.
 */
export function canDisqualify(entry: AdminEntryOut): boolean {
  return entry.status === 'ACTIVE';
}

/** The mirror: only a DISQUALIFIED entry can be reinstated. */
export function canReinstate(entry: AdminEntryOut): boolean {
  return entry.status === 'DISQUALIFIED';
}

// ── Error branching (fixed-vocabulary codes, never the English detail) ───────────────────────────

export const DEPOSIT_FUNDS_REQUIRED = 'DEPOSIT_FUNDS_REQUIRED';
export const PRIZE_BELOW_MINIMUM = 'PRIZE_BELOW_MINIMUM';

/** The underfunded-publish 409: the wallet's available balance is short of the prize. */
export function isDepositFundsRequired(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === DEPOSIT_FUNDS_REQUIRED;
}

/** The 422 prize-floor rejection (the backend truth behind {@link parsePrizeDollars}). */
export function isPrizeBelowMinimum(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === PRIZE_BELOW_MINIMUM;
}

/** The server's own copy when it supplied any, else the given fallback. Never invents a reason. */
export function errorMessage(error: unknown, fallback: string): string {
  return isGivmoError(error) ? (error as GivmoError).message : fallback;
}

// ── The Steps Sponsor DPA click-through (SC-GO2) ─────────────────────────────────────────────────
//
// Creating a competition is the moment a company binds itself as Givfi's PROCESSOR of participant
// health data, so an unbound company accepts the **Company Sponsor & Consumer-Health-Data Processing
// Addendum** here. The backend serves the state at `GET …/steps-competitions/sponsor-dpa` and
// re-serves the SAME shape on the create's 422 `SPONSOR_DPA_ACCEPTANCE_REQUIRED` at
// `errors[0].context.sponsor_dpa` — so a refused create renders the click-through off the refusal
// itself, with no second call. Both feed one decode; the iOS admin surface models it identically
// (`StepsSponsorDpaState`), so the two clients cannot drift on what "must I ask?" means.
//
// The route is NOT in the committed OpenAPI snapshot (it landed after the last regen), so the state
// is hand-modeled here and the create's additive request field is a typed extension over the
// generated `CreateBody` — the sanctioned ahead-of-a-regen pattern (agent_docs/api-client.md).

/**
 * The Sponsor DPA state for the acting company. Carries only what this surface renders or decides
 * on; the wire's acceptance-audit stamps (`accepted_by_user_id`, `accepted_at`, `content_hash`) are
 * deliberately not modeled — they are the server's record, and nothing here shows or echoes them.
 */
export interface SponsorDpaState {
  /** Whether the company has EVER accepted any version — forks "first time" from "a new version". */
  accepted: boolean;
  /**
   * Whether the company is bound to the CURRENT effective version. This — not {@link accepted} — is
   * the gate, and it is exactly the backend create gate's own predicate, so the client's "must I
   * show the click-through?" and the server's "may I create?" read the same field.
   */
  currentVersionAccepted: boolean;
  /** The current effective version number (`1` for the seeded v1); `0` when the wire omits it. */
  currentVersionNumber: number;
  /** When the current version became effective, as delivered; null when absent or blank. */
  currentEffectiveAt: string | null;
  /** The Addendum body to render — markdown as stored, the canonical bytes, never re-fetched. */
  content: string;
}

/**
 * Tolerant, FAIL-CLOSED decode of the Sponsor DPA state — from the state read OR the 422's
 * `context.sponsor_dpa`. Never throws.
 *
 * Every default fails toward REQUIRING acceptance: a junk/lean body decodes `currentVersionAccepted`
 * to `false`, which shows the click-through rather than silently skipping a legally-required
 * consent. `true` is honoured only as the literal boolean — a stringy `"true"` is not an acceptance.
 */
export function extractSponsorDpa(body: unknown): SponsorDpaState {
  const raw =
    typeof body === 'object' && body !== null && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : {};
  const effectiveAt = raw.current_effective_at;
  const versionNumber = raw.current_version_number;
  return {
    accepted: raw.accepted === true,
    currentVersionAccepted: raw.current_version_accepted === true,
    currentVersionNumber:
      typeof versionNumber === 'number' && Number.isFinite(versionNumber) ? versionNumber : 0,
    currentEffectiveAt:
      typeof effectiveAt === 'string' && effectiveAt.trim().length > 0 ? effectiveAt : null,
    content: typeof raw.content === 'string' ? raw.content : '',
  };
}

/**
 * Whether there is an Addendum body to actually put in front of the admin. A checkbox may only bind
 * a company to text it can read, so acceptance is never offered when this is false.
 */
export function sponsorDpaHasReadableContent(state: SponsorDpaState): boolean {
  return state.content.trim().length > 0;
}

/**
 * Whether the acting company must accept the Addendum as part of this create, and whether the body
 * is available to show while asking.
 *
 * `required` is the ONLY state that offers the checkbox or sends `sponsor_dpa_accepted`. `unreadable`
 * (state unreadable, or read but bodyless) states the requirement honestly WITHOUT collecting an
 * acceptance and does NOT block submit — the server stays authoritative and refuses with the 422
 * that hands back the body, turning a failed read into a real click-through.
 */
export type SponsorDpaGate =
  | { kind: 'satisfied' }
  | { kind: 'required'; state: SponsorDpaState }
  | { kind: 'unreadable' };

/** Resolve the gate from a decoded state, or from `null` when the state could not be read at all. */
export function resolveSponsorDpaGate(state: SponsorDpaState | null): SponsorDpaGate {
  if (state === null) return { kind: 'unreadable' };
  if (state.currentVersionAccepted) return { kind: 'satisfied' };
  if (!sponsorDpaHasReadableContent(state)) return { kind: 'unreadable' };
  return { kind: 'required', state };
}

export const SPONSOR_DPA_ACCEPTANCE_REQUIRED = 'SPONSOR_DPA_ACCEPTANCE_REQUIRED';

/** The create's 422 refusal: this company is not bound to the current Addendum version. */
export function isSponsorDpaAcceptanceRequired(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === SPONSOR_DPA_ACCEPTANCE_REQUIRED;
}

/**
 * The Addendum state the 422 carries at `errors[0].context.sponsor_dpa`, or null when the refusal
 * arrived without a usable one. Same decode as the state read — a recovery renders the identical
 * click-through.
 */
export function sponsorDpaFromError(error: unknown): SponsorDpaState | null {
  if (!isGivmoError(error)) return null;
  const payload = firstErrorContext(error)?.sponsor_dpa;
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  return extractSponsorDpa(payload);
}

// ── Rendering the stored Addendum ────────────────────────────────────────────────────────────────
//
// The body is markdown as counsel authored it, and it is rendered as delivered: line-per-paragraph
// with INLINE emphasis only. Block syntax stays literal deliberately — a legal instrument must
// render exactly the clauses it contains, and no parser may silently restructure, re-order, or drop
// one. iOS renders the same body under the same inline-only rule.

/** Split the stored Addendum into renderable paragraphs. Blank lines separate; nothing is dropped. */
export function sponsorDpaParagraphs(content: string): string[] {
  return content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** One run of Addendum text, bold or not. */
export interface SponsorDpaSpan {
  text: string;
  bold: boolean;
}

/**
 * Split a paragraph into bold/plain runs — but ONLY when every `**` marker on the line forms an
 * unambiguous, non-empty pair. An odd, adjacent (`***`), or empty (`****`) marker means the line is
 * returned verbatim as a single plain span: showing a stray asterisk is always safer than guessing
 * where emphasis was meant to start and stop in a document a company is about to be bound by.
 * (Same conservative rule the charity Terms presenter applies to its served document.)
 */
export function sponsorDpaSpans(line: string): SponsorDpaSpan[] {
  const literal = [{ text: line, bold: false }];
  const markers: number[] = [];
  for (let from = 0; ; ) {
    const marker = line.indexOf('**', from);
    if (marker === -1) break;
    if (line[marker - 1] === '*' || line[marker + 2] === '*') return literal;
    markers.push(marker);
    from = marker + 2;
  }
  if (markers.length === 0 || markers.length % 2 !== 0) return literal;

  const spans: SponsorDpaSpan[] = [];
  let cursor = 0;
  for (let index = 0; index < markers.length; index += 2) {
    const opening = markers[index]!;
    const closing = markers[index + 1]!;
    if (closing === opening + 2) return literal;
    if (opening > cursor) spans.push({ text: line.slice(cursor, opening), bold: false });
    spans.push({ text: line.slice(opening + 2, closing), bold: true });
    cursor = closing + 2;
  }
  if (cursor < line.length) spans.push({ text: line.slice(cursor), bold: false });
  return spans;
}

// ── The create body (typed extension ahead of a regen) ───────────────────────────────────────────

/**
 * The create body: the generated `CreateBody` plus SC-GO2's additive `sponsor_dpa_accepted`, which
 * is not in the committed snapshot yet. Typed as the LITERAL `true` so "never send `false`" is a
 * compile-time guarantee, not a convention — the field is sent exclusively when the admin ticked a
 * box against a body they were shown, and omitting it otherwise is what keeps this build creatable
 * against a backend that predates SC-GO2 (whose `extra="forbid"` create body rejects unknown keys).
 * The next snapshot regen absorbs the field; this declaration stays compatible.
 */
export type CreateCompetitionBody = CreateBody & { sponsor_dpa_accepted?: true };
